// Comprehensive funnel tracking utilities
import { trackEvent } from './mixpanel';
let sessionFallbackCounter = 0;

export interface FunnelStep {
  step: string;
  step_number: number;
  funnel_name: string;
  page: string;
  location?: string;
  button_text?: string;
  element_type?: string;
  user_path?: string;
  time_on_page?: number;
  scroll_depth?: number;
  additional_properties?: Record<string, unknown>;
}

export interface UserPath {
  entry_point: 'home' | 'impact' | 'direct' | 'external';
  path_type: 'standard' | 'impact_flow' | 'external_application';
  steps_completed: string[];
  current_step: string;
  time_started: string;
  last_activity: string;
}

// Track detailed funnel steps with enhanced context
export const trackFunnelStep = (stepData: FunnelStep) => {
  const enhancedStep = {
    ...stepData,
    timestamp: new Date().toISOString(),
    session_id: getSessionId(),
    user_agent: typeof window !== 'undefined' ? window.navigator.userAgent : undefined,
    referrer: typeof window !== 'undefined' ? document.referrer : undefined,
    url: typeof window !== 'undefined' ? window.location.href : undefined,
  };

  // Track as both funnel step and general event for flexibility
  trackEvent('Funnel Step', enhancedStep);
  trackEvent('User Journey', {
    action: stepData.step,
    page: stepData.page,
    location: stepData.location,
    button_text: stepData.button_text,
    element_type: stepData.element_type,
    user_path: stepData.user_path,
    ...stepData.additional_properties
  });
};

// Track button interactions with detailed context
export const trackButtonInteraction = (
  buttonText: string,
  location: string,
  page: string,
  additionalProps?: Record<string, unknown>
) => {
  trackFunnelStep({
    step: 'button_clicked',
    step_number: 0, // Will be overridden by specific step numbers
    funnel_name: 'user_interaction',
    page,
    location,
    button_text: buttonText,
    element_type: 'button',
    additional_properties: additionalProps
  });
};

// Track detailed button clicks for analysis
export const trackButtonClick = (
  buttonText: string,
  location: string,
  page: string,
  buttonType: 'cta' | 'navigation' | 'form' | 'cart' | 'external',
  additionalProps?: Record<string, unknown>
) => {
  // Track as dedicated button click event
  trackEvent('Button Clicked', {
    button_text: buttonText,
    button_type: buttonType,
    location: location,
    page: page,
    timestamp: new Date().toISOString(),
    session_id: getSessionId(),
    user_agent: typeof window !== 'undefined' ? window.navigator.userAgent : undefined,
    referrer: typeof window !== 'undefined' ? document.referrer : undefined,
    url: typeof window !== 'undefined' ? window.location.href : undefined,
    ...additionalProps
  });

  // Also track as funnel step for compatibility
  trackFunnelStep({
    step: 'button_clicked',
    step_number: 0,
    funnel_name: 'button_click_analysis',
    page,
    location,
    button_text: buttonText,
    element_type: 'button',
    additional_properties: {
      button_type: buttonType,
      ...additionalProps
    }
  });
};

// Track page interactions (scrolls, hovers, etc.)
export const trackPageInteraction = (
  interaction: string,
  element: string,
  page: string,
  additionalProps?: Record<string, unknown>
) => {
  trackFunnelStep({
    step: 'page_interaction',
    step_number: 0,
    funnel_name: 'user_engagement',
    page,
    location: element,
    element_type: interaction,
    additional_properties: additionalProps
  });
};

// Track form interactions
export const trackFormInteraction = (
  formName: string,
  action: string,
  page: string,
  additionalProps?: Record<string, unknown>
) => {
  trackFunnelStep({
    step: 'form_interaction',
    step_number: 0,
    funnel_name: 'form_engagement',
    page,
    location: formName,
    element_type: action,
    additional_properties: additionalProps
  });
};

// Track user path progression
export const trackUserPath = (
  currentStep: string,
  previousStep: string,
  page: string,
  pathType: 'standard' | 'impact_flow' | 'external_application'
) => {
  trackFunnelStep({
    step: 'path_progression',
    step_number: 0,
    funnel_name: 'user_path_analysis',
    page,
    location: currentStep,
    user_path: pathType,
    additional_properties: {
      previous_step: previousStep,
      path_type: pathType,
      progression: `${previousStep} -> ${currentStep}`
    }
  });
};

// Track cart interactions
export const trackCartInteraction = (
  action: 'add' | 'remove' | 'view' | 'proceed' | 'blocked',
  accountType: string,
  page: string,
  additionalProps?: Record<string, unknown>
) => {
  trackFunnelStep({
    step: 'cart_interaction',
    step_number: 0,
    funnel_name: 'account_selection',
    page,
    location: 'cart',
    element_type: action,
    additional_properties: {
      account_type: accountType,
      action,
      ...additionalProps
    }
  });
};

// Track conversion events
export const trackConversion = (
  conversionType: 'form_submission' | 'account_selection' | 'application_start',
  page: string,
  additionalProps?: Record<string, unknown>
) => {
  trackFunnelStep({
    step: 'conversion_event',
    step_number: 0,
    funnel_name: 'conversion_tracking',
    page,
    location: conversionType,
    element_type: 'conversion',
    additional_properties: additionalProps
  });
};

// Track drop-off points
export const trackDropOff = (
  dropOffPoint: string,
  page: string,
  reason?: string,
  additionalProps?: Record<string, unknown>
) => {
  trackFunnelStep({
    step: 'drop_off',
    step_number: 0,
    funnel_name: 'drop_off_analysis',
    page,
    location: dropOffPoint,
    element_type: 'drop_off',
    additional_properties: {
      reason,
      drop_off_point: dropOffPoint,
      ...additionalProps
    }
  });
};

// Generate session ID for tracking user sessions
const getSessionId = (): string => {
  if (typeof window === 'undefined') {return 'server-session';}
  
  let sessionId = sessionStorage.getItem('mixpanel_session_id');
  if (!sessionId) {
    sessionFallbackCounter += 1;
    const cryptoObj = window.crypto;
    const randomPart = cryptoObj?.randomUUID
      ? cryptoObj.randomUUID().replace(/-/g, '')
      : `${Date.now().toString(36)}_${sessionFallbackCounter.toString(36)}`;
    sessionId = `session_${randomPart}`;
    sessionStorage.setItem('mixpanel_session_id', sessionId);
  }
  return sessionId;
};

// Track time on page
export const trackTimeOnPage = (page: string, timeSpent: number) => {
  trackFunnelStep({
    step: 'time_on_page',
    step_number: 0,
    funnel_name: 'engagement_metrics',
    page,
    location: 'page',
    element_type: 'time_tracking',
    time_on_page: timeSpent,
    additional_properties: {
      time_spent_seconds: timeSpent,
      time_spent_minutes: Math.round(timeSpent / 60 * 100) / 100
    }
  });
};

// Track scroll depth
export const trackScrollDepth = (page: string, depth: number) => {
  trackFunnelStep({
    step: 'scroll_depth',
    step_number: 0,
    funnel_name: 'engagement_metrics',
    page,
    location: 'page',
    element_type: 'scroll_tracking',
    additional_properties: {
      scroll_depth_percentage: depth,
      scroll_depth_category: depth < 25 ? 'low' : depth < 75 ? 'medium' : 'high'
    }
  });
};
